import { ENABLE_CIRCLE_DRAWING_TOOL } from "../config/gridLinesConfig";
import useNavBarWidth from "../hooks/useNavBarWidth";
import {
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  FitScreen,
  Grid3x3,
  RectangleOutlined,
  RadioButtonUnchecked,
  PolylineOutlined,
  CropFree,
  Cancel,
} from "@mui/icons-material";
import { Box, IconButton, Typography, Paper, Button } from "@mui/material";
import React, { memo } from "react";

/**
 * PdfNavigationBar component for PDF controls
 * Contains page controls and zoom controls positioned at the top of the PDF
 */
const PdfNavigationBar = ({
  // Page Controls
  currentPage,
  totalPages,
  setCurrentPage,

  // Zoom Controls
  pdfScale,
  setPdfScale,

  // Fit to page
  onFitToPage,

  // Canvas ref for width calculation
  canvasRef,

  // Grid Controls
  showGrid,
  setShowGrid,
  selectedTable,

  // Table Structure Controls
  isPlacingGridLine,
  placingGridLineType,
  cancelGridLinePlacement,
  startAddNewRow,
  startAddNewColumn,
  isDraggingGridLine,

  // Drawing Tools
  drawingMode,
  setDrawingMode,
  showGridLineConfirmation,
  setShowGridLineConfirmation,
  cancelDrawing,
}) => {
  // Use hook for reactive navigation bar width calculation
  const { maxNavBarWidth } = useNavBarWidth(canvasRef);

  return (
    <Paper
      elevation={2}
      sx={{
        px: { xs: 0.75, sm: 1 },
        py: { xs: 0.25, sm: 0.5 },
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start", // Changed from center to flex-start to prevent left cutoff
        gap: { xs: 0.5, sm: 0.75 },
        borderRadius: 1,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        minWidth: "auto",
        maxWidth: maxNavBarWidth, // Dynamic width based on PDF container
        mb: 1, // Add margin bottom to separate from PDF content
        alignSelf: "center",
        width: "fit-content",
        overflowX: "auto", // Enable horizontal scrolling when content overflows
        overflowY: "hidden", // Prevent vertical overflow
        // Custom scrollbar styling
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
    >
      {/* Page Controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.375 },
          flex: "0 0 auto",
        }}
      >
        {/* Page Label */}
        <Typography
          variant={"caption"}
          sx={{
            fontSize: { xs: "0.6rem", sm: "0.65rem" },
            fontWeight: 600,
            color: "text.secondary",
            lineHeight: 1,
          }}
        >
          {"Page"}
        </Typography>

        {/* Page Navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.125, sm: 0.25 },
          }}
        >
          <IconButton
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            size={"small"}
            sx={{ p: 0.375 }}
          >
            <NavigateBefore fontSize={"small"} />
          </IconButton>
          <Typography
            variant={"body2"}
            sx={{
              minWidth: { xs: "35px", sm: "40px" },
              textAlign: "center",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {`${currentPage}/${totalPages}`}
          </Typography>
          <IconButton
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage >= totalPages}
            size={"small"}
            sx={{ p: 0.375 }}
          >
            <NavigateNext fontSize={"small"} />
          </IconButton>
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Zoom Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.125, sm: 0.25 },
          flex: "0 0 auto",
        }}
      >
        <IconButton
          onClick={() => setPdfScale(Math.max(0.3, pdfScale - 0.1))}
          size={"small"}
          sx={{ p: 0.375 }}
        >
          <ZoomOut fontSize={"small"} />
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            minWidth: { xs: "45px", sm: "50px" },
            textAlign: "center",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
        >
          {`${Math.round(pdfScale * 100)}%`}
        </Typography>
        <IconButton
          onClick={() => setPdfScale(Math.min(2.0, pdfScale + 0.1))}
          size={"small"}
          sx={{ p: 0.375 }}
        >
          <ZoomIn fontSize={"small"} />
        </IconButton>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Fit to Page */}
      <Button
        variant={"text"}
        size={"small"}
        onClick={onFitToPage}
        startIcon={<FitScreen fontSize={"small"} />}
        sx={{
          minWidth: "auto",
          px: { xs: 0.75, sm: 1 },
          py: { xs: 0.25, sm: 0.375 },
          fontSize: { xs: "0.65rem", sm: "0.7rem" },
          fontWeight: 500,
          textTransform: "none",
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
        }}
      >
        {"Fit"}
      </Button>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Grid Toggle */}
      <Box
        onClick={() => setShowGrid(!showGrid)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.5 },
          flex: "0 0 auto",
          cursor: "pointer",
          borderRadius: 1,
          p: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={showGrid ? "primary" : "default"}
          disabled={
            !selectedTable?.grid_lines ||
            (!selectedTable.grid_lines.bbox &&
              Object.keys(selectedTable.grid_lines).length === 0)
          }
          title={
            selectedTable?.grid_lines &&
            Object.keys(selectedTable.grid_lines).some((key) => key !== "bbox")
              ? `Toggle grid lines for current page`
              : "No grid lines available for this table"
          }
          sx={{ p: 0.375, pointerEvents: "none" }}
        >
          <Grid3x3 fontSize={"small"} />
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
            color: showGrid ? "primary.main" : "text.secondary",
          }}
        >
          {"Grid"}
        </Typography>
      </Box>

      {/* Add Row */}
      <Box
        onClick={
          isPlacingGridLine && placingGridLineType === "row"
            ? cancelGridLinePlacement
            : startAddNewRow
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.5 },
          flex: "0 0 auto",
          cursor: "pointer",
          borderRadius: 1,
          p: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={
            isPlacingGridLine && placingGridLineType === "row"
              ? "error"
              : "default"
          }
          disabled={
            isDraggingGridLine ||
            (isPlacingGridLine && placingGridLineType !== "row")
          }
          title={"Add Row"}
          sx={{
            p: 0.375,
            backgroundColor:
              isPlacingGridLine && placingGridLineType === "row"
                ? "rgba(244, 67, 54, 0.1)"
                : "transparent",
            border:
              isPlacingGridLine && placingGridLineType === "row"
                ? "1px solid #f44336"
                : "none",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            {isPlacingGridLine && placingGridLineType === "row" ? "✕" : "R+"}
          </Typography>
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
            color:
              isPlacingGridLine && placingGridLineType === "row"
                ? "error.main"
                : "text.secondary",
          }}
        >
          {"Add Row"}
        </Typography>
      </Box>

      {/* Add Column */}
      <Box
        onClick={
          isPlacingGridLine && placingGridLineType === "column"
            ? cancelGridLinePlacement
            : startAddNewColumn
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.5 },
          flex: "0 0 auto",
          cursor: "pointer",
          borderRadius: 1,
          p: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={
            isPlacingGridLine && placingGridLineType === "column"
              ? "error"
              : "default"
          }
          disabled={
            isDraggingGridLine ||
            (isPlacingGridLine && placingGridLineType !== "column")
          }
          title={"Add Column"}
          sx={{
            p: 0.375,
            backgroundColor:
              isPlacingGridLine && placingGridLineType === "column"
                ? "rgba(255, 152, 0, 0.1)"
                : "transparent",
            border:
              isPlacingGridLine && placingGridLineType === "column"
                ? "1px solid #ff9800"
                : "none",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            {isPlacingGridLine && placingGridLineType === "column" ? "✕" : "C+"}
          </Typography>
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
            color:
              isPlacingGridLine && placingGridLineType === "column"
                ? "warning.main"
                : "text.secondary",
          }}
        >
          {"Add Column"}
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Drawing Tools */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.375, sm: 0.5 },
          flex: "0 0 auto",
        }}
      >
        <IconButton
          onClick={() => {
            const newMode = drawingMode === "rectangle" ? null : "rectangle";
            if (showGridLineConfirmation) {
              setShowGridLineConfirmation(false);
            }
            setDrawingMode(newMode);
          }}
          size={"small"}
          color={drawingMode === "rectangle" ? "primary" : "default"}
          title={"Draw Rectangle"}
          sx={{ p: 0.375 }}
        >
          <RectangleOutlined fontSize={"small"} />
        </IconButton>

        {ENABLE_CIRCLE_DRAWING_TOOL && (
          <IconButton
            onClick={() => {
              const newMode = drawingMode === "circle" ? null : "circle";
              setDrawingMode(newMode);
            }}
            size={"small"}
            color={drawingMode === "circle" ? "primary" : "default"}
            title={"Draw Circle"}
            sx={{ p: 0.375 }}
          >
            <RadioButtonUnchecked fontSize={"small"} />
          </IconButton>
        )}

        <IconButton
          onClick={() => {
            const newMode = drawingMode === "polygon" ? null : "polygon";
            setDrawingMode(newMode);
          }}
          size={"small"}
          color={drawingMode === "polygon" ? "primary" : "default"}
          title={"Draw Polygon (click points, double-click to finish)"}
          sx={{ p: 0.375 }}
        >
          <PolylineOutlined fontSize={"small"} />
        </IconButton>

        <IconButton
          onClick={() => {
            const newMode = drawingMode === "freehand" ? null : "freehand";
            setDrawingMode(newMode);
          }}
          size={"small"}
          color={drawingMode === "freehand" ? "primary" : "default"}
          title={"Draw Freehand"}
          sx={{ p: 0.375 }}
        >
          <CropFree fontSize={"small"} />
        </IconButton>

        {drawingMode && (
          <IconButton
            onClick={cancelDrawing}
            size={"small"}
            color={"secondary"}
            title={"Cancel Drawing"}
            sx={{ p: 0.375 }}
          >
            <Cancel fontSize={"small"} />
          </IconButton>
        )}

        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
            color: drawingMode ? "primary.main" : "text.secondary",
          }}
        >
          {drawingMode ? `Drawing: ${drawingMode}` : "Draw Tools"}
        </Typography>
      </Box>
    </Paper>
  );
};

export default memo(PdfNavigationBar);
